<template>
  <!-- 顶栏独立主题切换：与用户菜单解耦，便于扩展多主题 -->
  <el-dropdown trigger="click" @command="handleThemeCommand">
    <button type="button" class="layoutThemePicker__trigger" aria-label="切换界面主题">
      <i class="el-icon-brush layoutThemePicker__icon" aria-hidden="true" />
      <span class="layoutThemePicker__label">{{ activeThemeLabel }}</span>
      <span v-if="!isThemePinned" class="layoutThemePicker__modeTag">随机</span>
      <span v-else class="layoutThemePicker__modeTag layoutThemePicker__modeTag--pinned">已固定</span>
      <i class="el-icon-arrow-down layoutThemePicker__caret" aria-hidden="true" />
    </button>
    <el-dropdown-menu slot="dropdown" class="layoutThemePicker__menu">
      <el-dropdown-item
        v-for="theme in cmsThemes"
        :key="theme.id"
        :command="theme.id"
        :disabled="!theme.available"
      >
        <span class="layoutThemePicker__item">
          <i
            class="layoutThemePicker__check el-icon-check"
            :class="{ 'layoutThemePicker__check--on': cmsThemeId === theme.id }"
            aria-hidden="true"
          />
          <span class="layoutThemePicker__itemLabel">
            {{ theme.label }}
            <span v-if="!theme.available" class="layoutThemePicker__badge">即将推出</span>
          </span>
        </span>
      </el-dropdown-item>
      <el-dropdown-item divided :command="CMS_THEME_RANDOM_COMMAND">
        <span class="layoutThemePicker__item">
          <i
            class="layoutThemePicker__check el-icon-check"
            :class="{ 'layoutThemePicker__check--on': !isThemePinned }"
            aria-hidden="true"
          />
          <span class="layoutThemePicker__itemLabel">
            每次打开随机
            <span class="layoutThemePicker__hint">未固定时，新会话自动换主题</span>
          </span>
        </span>
      </el-dropdown-item>
    </el-dropdown-menu>
  </el-dropdown>
</template>

<script>
import { CMS_THEMES, resolveCmsThemeMeta } from "./cmsThemeRegistry";
import {
  CMS_THEME_RANDOM_COMMAND,
  getCmsThemeId,
  isCmsThemePinned,
  setCmsTheme,
} from "./cmsThemeService";

/** 顶栏主题选择器：未固定时会话随机；用户选定后持久固定 */
export default {
  name: "LayoutThemePicker",
  data() {
    return {
      cmsThemeId: getCmsThemeId(),
      isThemePinned: isCmsThemePinned(),
      cmsThemes: CMS_THEMES,
      CMS_THEME_RANDOM_COMMAND,
    };
  },
  computed: {
    activeThemeLabel() {
      const meta = resolveCmsThemeMeta(this.cmsThemeId);
      return meta ? meta.label : "主题";
    },
  },
  methods: {
    handleThemeCommand(themeId) {
      const nextId = setCmsTheme(themeId);
      if (nextId) {
        this.cmsThemeId = nextId;
        this.isThemePinned = isCmsThemePinned();
      }
    },
  },
};
</script>

<style scoped>
.layoutThemePicker__trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  margin-right: 14px;
  padding: 0 10px;
  border: 1px solid var(--link-line-strong, #e3ded6);
  border-radius: var(--link-radius-sm, 8px);
  background: var(--link-card-surface, #ffffff);
  color: var(--link-ink-secondary, #57534e);
  font-family: var(--link-font-family);
  font-size: var(--link-text-sm, 12px);
  font-weight: var(--link-font-medium, 500);
  cursor: pointer;
  transition:
    border-color var(--link-dur, 200ms) var(--link-ease),
    background-color var(--link-dur, 200ms) var(--link-ease),
    color var(--link-dur, 200ms) var(--link-ease);
}

.layoutThemePicker__trigger:hover {
  border-color: var(--link-ink-muted, #a8a29e);
  background: var(--link-surface-muted, #faf9f7);
  color: var(--link-ink, #292524);
}

.layoutThemePicker__icon {
  font-size: 14px;
  color: var(--link-ink-muted, #a8a29e);
}

.layoutThemePicker__label {
  max-width: 88px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.layoutThemePicker__modeTag {
  flex-shrink: 0;
  padding: 1px 6px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: var(--link-font-medium, 500);
  color: var(--link-accent, #2563eb);
  background: var(--link-accent-soft, rgba(37, 99, 235, 0.08));
  border: 1px solid var(--link-accent-border, rgba(37, 99, 235, 0.2));
}

.layoutThemePicker__modeTag--pinned {
  color: var(--link-ink-tertiary, #78716c);
  background: var(--link-surface-muted, #faf9f7);
  border-color: var(--link-line, #eee9e2);
}

.layoutThemePicker__caret {
  font-size: 12px;
  color: var(--link-ink-muted, #a8a29e);
}

.layoutThemePicker__item {
  display: inline-flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 168px;
}

.layoutThemePicker__check {
  width: 14px;
  margin-top: 2px;
  font-size: 14px;
  color: transparent;
}

.layoutThemePicker__check--on {
  color: var(--link-ink-emphasis, #44403c);
}

.layoutThemePicker__itemLabel {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.layoutThemePicker__hint {
  font-size: 10px;
  font-weight: var(--link-font-regular, 400);
  color: var(--link-ink-muted, #a8a29e);
  line-height: 1.3;
}

.layoutThemePicker__badge {
  font-size: 11px;
  color: var(--link-ink-muted, #a8a29e);
}
</style>
